import { memo, useMemo, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { SingleValue } from 'react-select';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { Button } from '../../../../Controls/Button/index.tsx';
import Icons from '../../../../Elements/Icons/index.ts';
import { Select } from '../../../../Form/Select/index.tsx';
import type { Option } from '../../types.ts';
import { PageInfo } from '../PageInfo/index.tsx';
import { TableInfo } from '../TableInfo/index.tsx';
import { paginationPresetDefault, paginationPresetReduced } from './constants.ts';
import { PageInfoWrapper, Wrapper } from './styled.ts';
import type { TablePaginationProps } from './types.ts';

export const TablePagination: FC<TablePaginationProps> = memo(function TablePagination({
  className,
  info,
  pagination,
  isLoading,
  dataTestId,
}) {
  const isMobile = useMediaQuery({ query: Media.xsMax });
  const selectedPerPage = useMemo(
    (): Option<number> => ({ label: String(pagination.perPage), value: pagination.perPage }),
    [pagination.perPage],
  );
  const isReducedPaginationPreset = useMemo(
    () => !!paginationPresetReduced.find((option) => option.value === pagination.perPage),
    [pagination.perPage],
  );

  const handleSetPerPage = useCallbackRef((option: SingleValue<Option<number>>) => {
    if (option) {
      pagination.setPerPage(option.value);
      pagination.setPage(0);
    }
  });

  const nextPage = useCallbackRef(() => {
    pagination.setPage((prev) => prev + 1);
  });

  const prevPage = useCallbackRef(() => {
    pagination.setPage((prev) => prev - 1);
  });

  if (!isLoading && info.total <= pagination.perPage) {
    return <TableInfo total={info.total} isLoading={isLoading} />;
  }

  return (
    <Wrapper className={className} data-testid={dataTestId}>
      {!isMobile && (
        <div data-testid='per-page-dropdown'>
          <Select
            options={isReducedPaginationPreset ? paginationPresetReduced : paginationPresetDefault}
            value={selectedPerPage}
            onChange={handleSetPerPage}
            menuPlacement='auto'
            data-testid='select-op'
          />
        </div>
      )}
      <PageInfoWrapper>
        <PageInfo dataTestId='from-to-total-pagination' {...info} isLoading={isLoading} />
      </PageInfoWrapper>
      <Button secondary iconOnly onClick={prevPage} disabled={pagination.page === 0} data-testid='backward-button'>
        <Icons.Arrow align='left' />
      </Button>
      <Button
        secondary
        iconOnly
        onClick={nextPage}
        disabled={info.total === 0 || pagination.page === Math.ceil(info.total / pagination.perPage) - 1}
        data-testid='forward-button'>
        <Icons.Arrow align='right' />
      </Button>
    </Wrapper>
  );
});

export { usePageInfo } from './usePageInfo.ts';
export { usePagination } from './usePagination.ts';
